import { useRoute, stringifyQuery, type LocationQueryRaw } from 'vue-router';
import dayjs from 'dayjs';

const updateStoreParam = (
  router: any,
  store: any,
  setting?: string,
  value?: any,
  storeName: string = '',
) => {
  // Go back to Page 1 by default
  if (setting) {
    store.params.page = 1;
  }

  if (setting === 'tab') {
    store.params.tab = value;
  }

  if (setting === 'account') {
    if (storeName === 'documents') {
      delete store.params.account;

      if (value === -1) {
        delete store.params.account_id;
      } else {
        store.params.account_id = value;
      }
    } else {
      if (value === -1) {
        delete store.params.account;
      } else {
        store.params.account = value;
      }
    }
  }

  if (setting === 'page') {
    store.params.page = value;
  }

  if (setting === 'search') {
    if (storeName === 'accounts') {
      store.params.account_name = value;
    } else {
      store.params.search = value;
    }
  }

  if (setting === 'archive') {
    if (storeName === 'users') {
      store.params.archived = !store.params.archived;
    } else {
      store.params.include_inactive = !store.params.include_inactive;
    }
  }

  if (setting === 'type_filter') {
    store.params.type_filter = value;
    store.params.status_filter = 'all';
  }

  if (setting === 'status_filter') {
    store.params.status_filter = value;
  }

  updateURLParameters(router, store.params);
  store.getData(true);
};

const sortCol = (router: any, store: any, field: string, sortString: string = 'sort') => {
  store.params.page = 1;

  if (sortString === 'sort') {
    if (store.params.sort === field) {
      store.params.sortDesc = !store.params.sortDesc;
    } else {
      store.params.sort = field;
      store.params.sortDesc = false;
    }
  } else if (sortString === 'sortBy') {
    if (store.params.sortBy === field) {
      store.params.sortDesc = !store.params.sortDesc;
    } else {
      store.params.sortBy = field;
      store.params.sortDesc = false;
    }
  } else if (sortString === 'order') {
    if (store.params.order === field) {
      store.params.sortDesc = !store.params.sortDesc;
    } else {
      store.params.order = field;
      store.params.sortDesc = true;
    }
  }

  updateURLParameters(router, store.params);
  store.getData(true);
};

const getParamsOnLoad = (params: any) => {
  const route = useRoute();

  // default
  Object.keys(route.query).forEach(key => {
    switch(key) {
    // numeric
    case 'page':
    case 'results_per_page':
    case 'account':
    case 'account_id':
      params[key] = parseInt(`${route.query[key]}`);
      break;

      // dates
    case 'from':
    case 'to':
      params[key] = dayjs(`${route.query[key]}`, 'YYYY-MM-DD').toDate();
      break;

      // boolean
    case 'sortDesc':
    case 'include_inactive':
      params[key] = ['1', 'true'].includes(`${route.query[key]}`) ? true : false;
      break;

      // string
    default:
      params[key] = `${route.query[key]}`;
    }
  });

  return params;
};

// This combines the index queries for
// Event, Account, Sensor, User
type CombinedIndexQuery = {
  page: number;
  results_per_page: number;

  name?: string; // Event, Sensor, User
  account?: number | string; // Event, Sensor (string), User
  account_name?: string; // Account
  display_name?: string; // Event

  include_inactive?: boolean | number; // Event, Sensor (number), Account
  archived?: boolean; // User

  event_name?: string; // Event
  event?: string; // Sensor

  search?: string; // Event, Sensor

  from?: string | Date | null; // Event
  to?: string | Date | null; // Event
  start_date?: string; // Sensor
  end_date?: string; // Sensor

  live?: boolean; // Event
  upcoming?: boolean; // Event

  selected?: string; // Sensor
  location_id?: number; // Sensor

  account_status?: number[]; // Account

  sortDesc: boolean | number; // Number for Sensors
  sort?: string; // Event, Sensor
  sortBy?: string; // Event, User, Account

  sort_by?: string; // Event
  sort_desc?: boolean; // Event
};

const updateURLParameters = (router: any, params: any) => {
  const query: LocationQueryRaw = {};
  Object.keys(params).forEach((key) => {
    // Don't save "Account: Any"
    if (key === 'account' && params[key] === -1) {
      return;
    }

    if (params[key as keyof CombinedIndexQuery] !== undefined) {
      query[key] = `${params[key as keyof CombinedIndexQuery]}`;
    }
  });
  const qs = stringifyQuery(query);
  history.replaceState(history.state, '', window.location.pathname + (qs ? `?${qs}` : ''));
};

export { updateStoreParam, sortCol, getParamsOnLoad };
